import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { sowService } from '../services/sowService';
import { useAuthToken } from './AuthTokenProvider';
import { Images, ChevronLeft, ChevronRight, X, Expand, ChevronDown, ChevronUp, Info } from 'lucide-react';

interface ImageGalleryProps {
  sowId: string;
  className?: string;
  refreshTrigger?: number; // Change this value to trigger a refresh
  isPublicMode?: boolean; // Whether this is a public/unauthenticated view
}

interface SOWImage {
  id: string;
  imageId: string;
  filename: string;
  url: string;
  uploadedAt: string;
  timestamp?: string;
  analysisContext?: string;
  roomType?: string;
  description?: string;
  jobId?: string;
  jobLabel?: string;
  dataPoints?: {
    measurements?: any;
    materials?: any;
    conditions?: any;
    quantities?: any;
  };
  questionnaireContext?: {
    type: string;
    questionId?: string;
    component?: string;
  };
  analysis?: {
    description?: string;
    extractedData?: {
      visibleElements?: string[];
      datapoints?: { [key: string]: { value: any; confidence: number } };
    };
    status?: string;
    confidence?: number;
    feedback?: string;
    issues?: string[];
    dataPoints?: any;
  };
}

// Helper to get display label for an image
function getImageLabel(image: SOWImage): string {
  // Use room type if available (photos-first flow)
  if (image.roomType && image.roomType !== 'other') {
    return image.roomType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
  // Use job label if assigned
  if (image.jobLabel) {
    return image.jobLabel;
  }
  // Legacy: check questionnaireContext
  if (image.questionnaireContext?.questionId === 'kitchen_anchor_photo') {
    return 'Kitchen Overview';
  }
  if (image.analysisContext?.includes('Question:')) {
    return image.analysisContext.replace('Question: ', '').replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
  }
  // Fallback to description snippet
  if (image.description) {
    return image.description.split(/[,.]/).shift()?.trim() || 'Photo';
  }
  return 'Photo';
}

// Helper to get description — check both top-level and analysis.description
function getImageDescription(image: SOWImage): string | undefined {
  return image.description || image.analysis?.description;
}

// Helper to get dataPoints — check both top-level and analysis.dataPoints
function getImageDataPoints(image: SOWImage): any | undefined {
  return image.dataPoints || image.analysis?.dataPoints;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({ sowId, className = '', refreshTrigger, isPublicMode = false }) => {
  const { tokenSet, isLoading: tokenLoading } = useAuthToken();
  const [images, setImages] = useState<SOWImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<SOWImage | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showAnalysisDetails, setShowAnalysisDetails] = useState(false);

  useEffect(() => {
    // In public mode, load immediately
    // In auth mode, wait for token
    if (sowId && (isPublicMode || (tokenSet && !tokenLoading))) {
      loadImages();
    }
  }, [sowId, tokenSet, tokenLoading, refreshTrigger, isPublicMode]);

  const loadImages = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the proper SOW images endpoint that returns images with signed URLs
      const response = await sowService.getSOWImages(sowId, !isPublicMode);
      const images: SOWImage[] = response.images || [];

      setImages(images);
    } catch (error) {
      console.error('Error loading images:', error);
      setError('Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  const openImageModal = (image: SOWImage, index: number) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setShowInfoPanel(false);
    setShowAnalysisDetails(false);
  };

  const goToPrevious = () => {
    if (images.length > 0) {
      const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
      setCurrentIndex(newIndex);
      setSelectedImage(images[newIndex]);
    }
  };

  const goToNext = () => {
    if (images.length > 0) {
      const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
      setCurrentIndex(newIndex);
      setSelectedImage(images[newIndex]);
    }
  };

  if (loading || tokenLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Images className="h-5 w-5 text-blue-600" />
            <h3 className="font-medium text-gray-900">Uploaded Photos</h3>
          </div>
          <div className="text-center py-8">
            <div className="text-sm text-gray-500">Loading images...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Images className="h-5 w-5 text-blue-600" />
            <h3 className="font-medium text-gray-900">Uploaded Photos</h3>
          </div>
          <div className="text-center py-8">
            <div className="text-sm text-red-500">{error}</div>
            <Button
              onClick={loadImages}
              variant="outline"
              size="sm"
              className="mt-3"
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (images.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Images className="h-5 w-5 text-blue-600" />
            <h3 className="font-medium text-gray-900">Uploaded Photos</h3>
          </div>
          <div className="text-center py-8">
            <Images className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <div className="text-sm text-gray-500">No photos uploaded yet</div>
            <div className="text-xs text-gray-400 mt-1">
              Photos will appear here as you upload them during the questionnaire
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Images className="h-5 w-5 text-blue-600" />
              <h3 className="font-medium text-gray-900">Uploaded Photos</h3>
            </div>
            <div className="text-xs text-gray-500">
              {images.length} photo{images.length !== 1 ? 's' : ''}
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((image, index) => (
              <div
                key={image.id}
                className="relative group cursor-pointer rounded-lg overflow-hidden border border-gray-200 hover:border-blue-300 transition-colors"
                onClick={() => openImageModal(image, index)}
              >
                <div className="aspect-square bg-gray-50 flex items-center justify-center">
                  <img
                    src={image.url}
                    alt={image.filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <Expand className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                  <div className="text-xs text-white truncate">
                    {getImageLabel(image)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed !top-0 !left-0 !right-0 !bottom-0 bg-black/90 z-50 !m-0" style={{ margin: 0 }}>
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Top Controls Bar */}
            <div className="absolute top-4 right-4 z-30 flex items-center gap-2">
              {/* Info Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowInfoPanel(!showInfoPanel)}
                className="bg-white hover:bg-gray-100 text-gray-700 rounded-full p-2 h-10 w-10 shadow-lg"
              >
                <Info className="h-5 w-5" />
              </Button>

              {/* Close button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={closeImageModal}
                className="bg-white hover:bg-gray-100 text-gray-700 rounded-full p-2 h-10 w-10 shadow-lg"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Navigation buttons */}
            {images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goToPrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 h-10 w-10 shadow-lg"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goToNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 h-10 w-10 shadow-lg"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </>
            )}

            {/* Image - Full Screen */}
            <div className="w-full h-full flex items-center justify-center px-4">
              <img
                src={selectedImage.url}
                alt={selectedImage.filename}
                className="max-w-full max-h-full object-contain"
              />
            </div>

            {/* Info Panel Drawer - Slides in from right */}
            <div
              className={`absolute top-0 right-0 h-full w-full sm:w-96 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-40 ${
                showInfoPanel ? 'translate-x-0' : 'translate-x-full'
              }`}
            >
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 text-base">
                      {getImageLabel(selectedImage)}
                    </h4>
                    {images.length > 1 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Photo {currentIndex + 1} of {images.length}
                      </div>
                    )}
                    {(selectedImage.uploadedAt || selectedImage.timestamp) && (
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(selectedImage.uploadedAt || selectedImage.timestamp || '').toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInfoPanel(false)}
                    className="text-gray-500 hover:text-gray-700 p-1 h-8 w-8 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {getImageDescription(selectedImage) && (
                    <p className="text-sm text-gray-700 leading-relaxed">{getImageDescription(selectedImage)}</p>
                  )}

                  {/* Data Points from photos-first analysis */}
                  {getImageDataPoints(selectedImage) && (
                    <div className="space-y-3">
                      {getImageDataPoints(selectedImage)?.materials && Object.keys(getImageDataPoints(selectedImage).materials).length > 0 && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="text-xs font-semibold text-blue-900 mb-2">Materials</div>
                          <div className="space-y-1.5">
                            {Object.entries(getImageDataPoints(selectedImage).materials).map(([key, val]: [string, any]) => (
                              <div key={key} className="flex justify-between text-xs">
                                <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
                                <span className="text-gray-900 font-medium text-right max-w-[60%]">
                                  {typeof val === 'object' && val !== null
                                    ? Object.entries(val).map(([k, v]) => `${k}: ${v}`).join(', ')
                                    : String(val)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {getImageDataPoints(selectedImage)?.conditions && (
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="text-xs font-semibold text-amber-900 mb-2">Conditions</div>
                          <div className="space-y-1.5">
                            {getImageDataPoints(selectedImage).conditions.overall_condition && (
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-600">Overall</span>
                                <span className="text-gray-900 font-medium capitalize">{getImageDataPoints(selectedImage).conditions.overall_condition}</span>
                              </div>
                            )}
                            {getImageDataPoints(selectedImage).conditions.visible_damage?.length > 0 && (
                              <div>
                                <div className="text-xs text-amber-800 font-medium mb-1">Visible Damage:</div>
                                <ul className="text-xs text-amber-700 space-y-0.5">
                                  {getImageDataPoints(selectedImage).conditions.visible_damage.map((d: string, i: number) => (
                                    <li key={i} className="flex items-start gap-1">
                                      <span className="mt-0.5">•</span> {d}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Legacy Analysis Details Toggle - Show if analysis object exists */}
                  {selectedImage.analysis && (
                    <>
                      <button
                        onClick={() => setShowAnalysisDetails(!showAnalysisDetails)}
                        className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-lg border border-blue-200 transition-colors"
                      >
                        <span className="text-sm font-semibold text-blue-900">AI Analysis Details</span>
                        {showAnalysisDetails ? (
                          <ChevronUp className="h-4 w-4 text-blue-700" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-blue-700" />
                        )}
                      </button>

                      {/* Analysis Details - Collapsible */}
                      {showAnalysisDetails && (
                        <div className="space-y-4">
                          {/* Analysis Status */}
                          {selectedImage.analysis.status && selectedImage.analysis.status !== 'pending' && (
                            <div className={`text-sm px-3 py-2 rounded-lg inline-flex items-center gap-2 ${
                              selectedImage.analysis.status === 'accepted' ? 'bg-green-100 text-green-800' :
                              selectedImage.analysis.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              <span className={`w-2 h-2 rounded-full ${
                                selectedImage.analysis.status === 'accepted' ? 'bg-green-600' :
                                selectedImage.analysis.status === 'rejected' ? 'bg-red-600' :
                                'bg-yellow-600'
                              }`}></span>
                              {selectedImage.analysis.status === 'accepted' ? 'Analysis Complete' :
                               selectedImage.analysis.status === 'rejected' ? 'Needs Review' :
                               'In Progress'}
                            </div>
                          )}

                          {/* Feedback */}
                          {selectedImage.analysis.feedback && (
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <div className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-1.5">
                                💡 Analysis
                              </div>
                              <div className="text-sm text-gray-700 leading-relaxed">{selectedImage.analysis.feedback}</div>
                            </div>
                          )}

                          {/* Issues */}
                          {selectedImage.analysis.issues && selectedImage.analysis.issues.length > 0 && (
                            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                              <div className="text-xs font-semibold text-amber-900 mb-2 flex items-center gap-1.5">
                                ⚠️ Considerations
                              </div>
                              <ul className="text-sm text-amber-800 space-y-1.5">
                                {selectedImage.analysis.issues.map((issue, index) => (
                                  <li key={index} className="flex items-start gap-2">
                                    <span className="text-amber-600 mt-0.5 text-xs">•</span>
                                    <span className="leading-relaxed">
                                      {issue.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Extracted Elements */}
                          {selectedImage.analysis.extractedData?.visibleElements && selectedImage.analysis.extractedData.visibleElements.length > 0 && (
                            <div>
                              <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                                🏠 Features Identified
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {selectedImage.analysis.extractedData.visibleElements.map((element, index) => (
                                  <span key={index} className="text-xs bg-white text-blue-700 px-2.5 py-1 rounded-full border border-blue-200 font-medium">
                                    {element.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Extracted Datapoints */}
                          {((selectedImage.analysis.extractedData?.datapoints && Object.keys(selectedImage.analysis.extractedData.datapoints).length > 0) ||
                            (selectedImage.analysis.dataPoints && Object.keys(selectedImage.analysis.dataPoints).length > 0)) && (
                            <div>
                              <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                                📏 Measurements
                              </div>
                              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <div className="space-y-2">
                                  {Object.entries(selectedImage.analysis.extractedData?.datapoints || selectedImage.analysis.dataPoints || {}).map(([key, datapoint], index) => (
                                    <div key={index} className="flex justify-between items-center text-sm border-b border-gray-200 pb-2 last:border-0 last:pb-0">
                                      <span className="text-gray-600 capitalize text-xs">
                                        {key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')}
                                      </span>
                                      <span className="text-gray-900 font-semibold">
                                        {typeof datapoint === 'object' && datapoint && 'value' in datapoint ? (datapoint as any).value : datapoint}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};